import { createContext } from 'react';
import { decorate, observable, action, computed, toJS } from 'mobx';

import api from '../api';
import * as fn from '../utilities/_functions';

const PAGE_SIZE = 1000;
const FULL_SEARCH_SIZE = 1000;

export class ServiceAttributeSearch {
    page = 1;
    pageSize = PAGE_SIZE;
    searchTerm = null;
    services = [];
    overrides = [];
    attributes = [];
    total = null;
    isLoading = false;
    isSaving = false;
    isReady = false;

    cancelServiceDelete = null;
    cancelServiceFullSearch = null;
    cancelServiceSearch = null;
    cancelServiceSearchCount = null;
    cancelServiceTypes = null;

    initialize = async (pageSize = PAGE_SIZE) => {
        this.clear();
        this.pageSize = pageSize;

        await this.refresh();

        this.isReady = true;
    }

    execute = async (page, notify = true) => {
        const offset = page ? ((page - 1) * this.pageSize) : 0;

        this.page = page;

        if (notify) {
            this.isLoading = true;
        }

        try {
            const { data } = await api.ServiceAttributes.fullSearch('', this.pageSize, offset, (c) => { this.cancelServiceSearch = c });
            action(e => {
                this.services = data.result.item1;
                this.attributes = data.result.item2;
                this.overrides = data.result.item3;
                this.total = data.total;
            })();
        }
        finally {
            if (notify) {
                this.isLoading = false;
            }
        }
    }

    reload = async (id, notify = true) => {
        if (notify) {
            this.isLoading = true;
        }

        try {
            const { data } = await api.ServiceAttributes.getForReload(id, (c) => { this.cancelServiceSearch = c });
            // combine the newly updated service data to previous result sets
            const attr = toJS(this.attributes).filter(s => s.serviceId !== id);
            const ovrd = toJS(this.overrides).filter(s => s.serviceId !== id);

            action(e => {
                this.attributes = attr.concat(data.result.item2);
                this.overrides = ovrd.concat(data.result.item3);

                const index = this.services.findIndex(s => s.id === id);
                if (data.result.item1.length) { // item1 array is empty if this is a delete operation
                    if (index >= 0)  // UPDATE
                        this.services[index] = data.result.item1[0];
                    else // INSERT
                        this.services.unshift(data.result.item1[0]);

                    this.services = [...this.services]; // force mobx update
                }
                else
                    this.services.splice(index, 1);

            })();
        }
        finally {
            if (notify) {
                this.isLoading = false;
            }
        }
    }

    delete = (serviceIds) => {
        const that = this;

        this.isSaving = true;

        return new Promise((resolve, reject) => {
            Promise.all(
                [...serviceIds].map(p => {
                    return api.Services.delete(p.id, (c) => { that.cancelServiceDelete = c })
                })
            )
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {
                    that.isSaving = false;
                })
        })
    }

    refresh = async () => {
        if (this.searchTerm) {
            return await this.search();
        }

        return await this.execute(this.page, false);
    }

    search = async () => {
        this.isLoading = true;
        try {
            const { data } = await api.ServiceAttributes.fullSearch(this.searchTerm, FULL_SEARCH_SIZE, 0, (c) => { this.cancelServiceFullSearch = c });
            action(e => {
                this.services = data.result.item1;
                this.attributes = data.result.item2;
                this.overrides = data.result.item3;
                this.total = data.total;
            })();
        }
        finally {
            this.isLoading = false;
        }
    }

    delete = (serviceIds) => {
        const that = this;

        this.isSaving = true;

        return new Promise((resolve, reject) => {
            Promise.all(
                that.services.filter(p => serviceIds.some(i => i === p.id)).map(p => {
                    return api.Services.delete(p.id, (c) => { that.cancelServiceDelete = c })
                })
            )
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {
                    that.isSaving = false;
                })
        })
    }

    clear = () => {
        this.page = 1;
        this.services.clear();
        this.overrides.clear();
        this.attributes.clear();

        this.total = null;
        this.isLoading = false;
        this.isReady = false;

        if (fn.isFunction(this.cancelServiceDelete)) {
            this.cancelServiceDelete();
            this.cancelServiceDelete = null;
        }

        if (fn.isFunction(this.cancelServiceFullSearch)) {
            this.cancelServiceFullSearch();
            this.cancelServiceFullSearch = null;
        }

        if (fn.isFunction(this.cancelServiceSearch)) {
            this.cancelServiceSearch();
            this.cancelServiceSearch = null;
        }

        if (fn.isFunction(this.cancelServiceSearchCount)) {
            this.cancelServiceSearchCount();
            this.cancelServiceSearchCount = null;
        }

        if (fn.isFunction(this.cancelServiceTypes)) {
            this.cancelServiceTypes();
            this.cancelServiceTypes = null;
        }
    }

    get pageIndex() {
        return this.page - 1;
    }

    get pageCount() {
        return Math.ceil(this.total / this.pageSize);
    }
}

decorate(ServiceAttributeSearch, {
    page: observable,
    pageSize: observable,
    searchTerm: observable,
    services: observable.deep,
    attributes: observable.deep,
    overrides: observable.deep,
    total: observable,
    display: observable,
    isLoading: observable,
    isSaving: observable,
    isReady: observable,
    initialize: action,
    execute: action,
    refresh: action,
    reload: action,
    search: action,
    refreshTotal: action,
    delete: action,
    clear: action,
    pageIndex: computed,
    pageCount: computed,
})

export { PAGE_SIZE, FULL_SEARCH_SIZE };
export default createContext(new ServiceAttributeSearch());